import React, { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useProducts, useProductStages, type Product, type PipelineStage } from '../hooks/useProducts';
import { getProgramByRoute, type ProgramConfig } from '../config/programs';

interface ProductContextValue {
  currentProduct: Product | null;
  programConfig: ProgramConfig | null;
  productCode: string;
  currentStages: PipelineStage[];
  stagesLoading: boolean;
  features: Record<string, boolean>;
  statusField: string;
  gradient: string;
  isProductPage: boolean;
}

const ProductContext = createContext<ProductContextValue>({
  currentProduct: null,
  programConfig: null,
  productCode: '',
  currentStages: [],
  stagesLoading: false,
  features: {},
  statusField: 'status',
  gradient: 'from-blue-600 to-indigo-600',
  isProductPage: false,
});

export const ProductProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { products } = useProducts();

  const resolved = useMemo(() => {
    const programConfig = getProgramByRoute(location.pathname);
    if (!programConfig) return { productCode: '', programConfig: null, product: null };

    const product = products.find(p => p.code === programConfig.serviceType) || null;
    return { productCode: programConfig.serviceType, programConfig, product };
  }, [location.pathname, products]);

  const { stages, loading: stagesLoading } = useProductStages(resolved.productCode);

  const value = useMemo<ProductContextValue>(() => ({
    currentProduct: resolved.product,
    programConfig: resolved.programConfig,
    productCode: resolved.productCode,
    currentStages: stages,
    stagesLoading,
    features: resolved.product?.features || resolved.programConfig?.features || {},
    statusField: resolved.product?.crm_status_field || resolved.programConfig?.kanbanStatusField || 'status',
    gradient: resolved.product?.gradient || resolved.programConfig?.gradient || 'from-blue-600 to-indigo-600',
    isProductPage: !!resolved.productCode,
  }), [resolved, stages, stagesLoading]);

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
};

export const useCurrentProduct = () => useContext(ProductContext);
